import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Smartphone, Globe, Leaf, TrendingUp, Users, Zap, Plus, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function DigitalCardsPage() {
  const navigate = useNavigate();
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/public/card-categories`);
      const data = await response.json();
      setCategories(data.categories || []);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch card categories:', err);
      setLoading(false);
    }
  };

  // Color variations for categories
  const bgColors = [
    'bg-blue-100',
    'bg-pink-100', 
    'bg-yellow-100',
    'bg-purple-100',
    'bg-green-100',
    'bg-orange-100'
  ];

  const faqs = [
    {
      question: 'What information can I include on my digital card?',
      answer: 'You can include contact details, social media links, portfolio, website, bio, photos, videos, and any other links you want to share. Your profile is fully customizable.',
    },
    {
      question: 'Do I need to buy a new card if my information changes?',
      answer: 'No! Your digital profile can be updated anytime through our platform. Your physical card stays the same, but the information it links to can be changed instantly.',
    },
    {
      question: 'Are digital cards compatible with all smartphones?',
      answer: 'Yes! NFC works with all modern smartphones (iPhone and Android). For older devices, we include a QR code as a backup option.',
    },
    {
      question: 'Can I track who views my digital card?',
      answer: 'Yes! You get analytics showing how many people tapped your card, when, and which links they clicked. Perfect for networking insights.',
    },
    {
      question: 'How long does the digital profile last?',
      answer: 'Your digital profile is lifetime access. As long as you maintain your account, your card will continue to work.',
    },
    {
      question: 'Can I order cards for my entire team?',
      answer: 'Absolutely! We offer bulk discounts for teams and organizations. Contact us for custom team solutions with centralized management.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl lg:text-6xl font-extrabold text-navy mb-6">
            Digital Business Cards
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Your professional identity in one tap. Modern NFC business cards that open your digital profile instantly.
          </p>
          <div className="flex justify-center gap-4">
            <Button className="bg-navy text-white hover:bg-navy/90 rounded-full px-8 py-6 text-base font-semibold">
              Get Your Card Now
            </Button>
            <Button variant="outline" className="border-2 border-navy text-navy hover:bg-navy hover:text-white rounded-full px-8 py-6 text-base font-semibold">
              View Demo Profile
            </Button>
          </div>
        </div>
      </section>

      {/* What are Digital Cards Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Text */}
            <div>
              <h2 className="text-4xl font-extrabold text-navy mb-6">
                What are Digital Business Cards?
              </h2>
              <div className="space-y-4 text-lg text-gray-700">
                <p>
                  Digital Business Cards are NFC-enabled smart cards that instantly share your 
                  professional profile with a single tap. No apps needed - just tap your card 
                  to any smartphone to open your personalized web page.
                </p>
                <p>
                  Share your contact information, social media, portfolio, videos, documents, 
                  and more - all in one beautiful, always up-to-date digital profile. Update 
                  your information anytime without printing new cards.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-6 mt-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-navy mb-2">1 Tap</div>
                  <div className="text-sm text-gray-600">To Share</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-navy mb-2">∞</div>
                  <div className="text-sm text-gray-600">Updates</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-navy mb-2">100%</div>
                  <div className="text-sm text-gray-600">Eco-Friendly</div>
                </div>
              </div>
            </div>

            {/* Right - Image */}
            <div className="relative">
              <div className="rounded-[2.5rem] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=800&h=600&fit=crop"
                  alt="Digital Business Card"
                  className="rounded-2xl shadow-lg w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Card Types Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-extrabold text-navy mb-4">
              Choose Your Style
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Premium quality cards designed to make the perfect first impression
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading card categories...</p>
            </div>
          ) : categories.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-8">
              {categories.map((category, index) => (
                <div 
                  key={category.id} 
                  className={`${bgColors[index % bgColors.length]} rounded-[2.5rem] p-8 relative overflow-hidden min-h-[550px] flex flex-col`}
                >
                  <div className="flex-1">
                    <h3 className="text-3xl font-bold text-navy mb-3">
                      {category.name}
                    </h3>
                    <p className="text-sm text-gray-600 mb-6">
                      {category.description}
                    </p>

                    {/* Card Image */}
                    {category.image && (
                      <div className="mb-6 flex justify-center items-center h-40">
                        <img 
                          src={`${BACKEND_URL}${category.image}`}
                          alt={category.name}
                          className="rounded-xl shadow-lg object-cover max-h-40"
                        />
                      </div>
                    )}

                    {/* Features */}
                    {category.features && category.features.length > 0 && (
                      <ul className="space-y-3 mb-6">
                        {category.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <CheckCircle className="w-5 h-5 text-navy flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* Price */}
                  <div className="mt-auto pt-4">
                    <div className="text-4xl font-bold text-navy mb-4">
                      ${category.starting_from}
                    </div>
                    <Button 
                      onClick={() => navigate(`/digital-cards/category/${category.id}`)}
                      className="w-full bg-navy text-white hover:bg-navy/90 py-6 rounded-2xl text-lg font-semibold"
                    >
                      View All Cards
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-600">No card categories available at the moment.</p>
            </div>
          )}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Image */}
            <div className="relative order-2 lg:order-1">
              <div className="bg-purple-custom rounded-[2.5rem] p-12 min-h-[500px] flex items-center justify-center">
                <img
                  src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&h=600&fit=crop"
                  alt="Professional Networking"
                  className="rounded-2xl shadow-lg w-full h-auto"
                />
              </div>
            </div>

            {/* Right - Text */}
            <div className="order-1 lg:order-2">
              <h2 className="text-4xl font-extrabold text-navy mb-6">
                Why Go Digital?
              </h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'hsl(220, 40%, 88%)' }}>
                    <Smartphone className="w-6 h-6 text-navy" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-navy mb-2">
                      Instant Connectivity
                    </h3>
                    <p className="text-gray-600">
                      Share your complete professional profile in seconds. No typing, no apps, 
                      just tap and connect. Works with iPhone and Android.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-yellow-custom rounded-xl flex items-center justify-center flex-shrink-0">
                    <Globe className="w-6 h-6 text-navy" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-navy mb-2">
                      Always Up-to-Date
                    </h3>
                    <p className="text-gray-600">
                      Update your contact info, add new projects, or refresh your bio anytime. 
                      Your card always shows the latest information.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Leaf className="w-6 h-6 text-navy" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-navy mb-2">
                      Eco-Friendly Choice
                    </h3>
                    <p className="text-gray-600">
                      One digital card replaces hundreds of paper cards. Reduce waste and 
                      embrace sustainable networking.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-purple-custom rounded-xl flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-6 h-6 text-navy" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-navy mb-2">
                      Track Your Network
                    </h3>
                    <p className="text-gray-600">
                      See who views your profile, which links they click, and when. 
                      Gain insights into your networking effectiveness.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-extrabold text-navy mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Everything you need to know about Digital Business Cards
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {faqs.map((faq, index) => (
              <div key={index} className="border-b border-gray-200 pb-4">
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full flex items-center justify-between text-left py-4"
                >
                  <span className="text-lg font-semibold text-navy pr-4">
                    {faq.question}
                  </span>
                  <Plus 
                    className={`w-6 h-6 text-navy flex-shrink-0 transition-transform ${
                      openFaqIndex === index ? 'rotate-45' : ''
                    }`}
                  />
                </button>
                {openFaqIndex === index && (
                  <p className="text-gray-600 pb-4">
                    {faq.answer}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
